import { useState } from "react";

import {
  buildCreateEntry,
  createEntryText,
  isCreateEntry,
  isNewName,
  matchesText,
} from "ui/creatable-select/creatable-select.helpers";

import type { CreatableSelectProps } from "ui/creatable-select/creatable-select.types";
import type { FilterOptionOption, OptionType } from "ui/select/select.types";

type UseCreatableSelectProps<Option extends OptionType> = Pick<
  CreatableSelectProps<Option>,
  "options" | "onChange" | "onCreateOption" | "filterOption"
>;

/**
 * The state behind `CreatableSelect`: the typed text, the list with a `Create '…'` entry appended
 * while that text names no option, and the two handlers that keep the entry apart from real options.
 *
 * The list is filtered here rather than by MUI's default, which does not trim: `"Gamma "` would filter
 * out the very entry it offers. The entry always passes; the rest go through the call site's
 * `filterOption`, or MUI's default match on the trimmed text.
 */
export const useCreatableSelect = <Option extends OptionType>({
  options,
  onChange,
  onCreateOption,
  filterOption,
}: UseCreatableSelectProps<Option>) => {
  const [inputValue, setInputValue] = useState("");
  const text = inputValue.trim();

  // A plain `OptionType`, not the call site's `Option`: it is only rendered, and comes back through
  // `handleChange`, which never hands it on.
  const listedOptions = isNewName(options, text)
    ? [...options, buildCreateEntry(text) as unknown as Option]
    : options;

  const keepOption = (candidate: FilterOptionOption<Option>, input: string) => {
    if (isCreateEntry(candidate.data)) {
      return true;
    }

    return filterOption ? filterOption(candidate, input) : matchesText(candidate.label, input);
  };

  const handleChange = (selected: Option | null) => {
    if (isCreateEntry(selected)) {
      onCreateOption(createEntryText(selected));

      return;
    }

    onChange?.(selected);
  };

  return {
    listedOptions,
    keepOption,
    handleChange,
    handleInputChange: setInputValue,
  };
};
